import React from 'react';
import { ArrowLeft, Trash2, Archive, Star, Reply, ReplyAll, Forward, MoreVertical, Download, FileText, Image as ImageIcon, ChevronDown, RotateCcw } from 'lucide-react';

import { motion, AnimatePresence } from 'framer-motion';
import { API_BASE } from '../../services/api';
import { formatDate } from '../../utils';

const MailViewer = ({ email, user, onBack, onAction, onReply, onReplyAll, onForward, onContextMenu }) => {

    const [showDetails, setShowDetails] = React.useState(false);
    if (!email) return null;

    const isSent = email.folder === 'Sent';

    // True identities from backend properties
    const rawFrom = email.from || user.email;
    const rawTo = email.to || email.recipient || user.email;

    const fromEmail = rawFrom.includes('<')
        ? rawFrom.substring(rawFrom.indexOf('<') + 1, rawFrom.indexOf('>'))
        : rawFrom;

    const toEmail = rawTo.includes('<')
        ? rawTo.substring(rawTo.indexOf('<') + 1, rawTo.indexOf('>'))
        : rawTo;

    // Primary display for the header (Who sent this?)
    let displayIdentifier = "";
    if (rawFrom.includes('<')) {
        displayIdentifier = rawFrom.split('<')[0].trim();
    } else {
        const username = fromEmail.includes('@') ? fromEmail.split('@')[0] : fromEmail;
        displayIdentifier = username.charAt(0).toUpperCase() + username.slice(1);
        if (displayIdentifier.length > 4) {
            displayIdentifier = displayIdentifier.replace(/[0-9]+$/, '');
        }
    }
    const senderInitial = displayIdentifier.charAt(0).toUpperCase();

    // Contextual recipient for the "to ..." subtext
    const recipientLabel = isSent ? toEmail : "me";


    return (
        <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            onContextMenu={(e) => {
                if (onContextMenu) {
                    e.preventDefault();
                    onContextMenu(e);
                }
            }}
            style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: 'transparent',
                height: '100%',
                overflow: 'hidden'
            }}
        >
            {/* Toolbar */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                padding: '16px 20px',
                borderBottom: '1px solid var(--border-glass)',
                justifyContent: 'space-between'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <motion.button
                        whileHover={{ x: -4 }}
                        onClick={onBack}
                        className="btn-ghost"
                        style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px' }}
                    >
                        <ArrowLeft size={18} /> <span className="hide-mobile" style={{ fontWeight: '600', fontSize: '0.9rem' }}>Back</span>
                    </motion.button>
                </div>

                <div style={{ display: 'flex', gap: '4px' }}>
                    {email.folder === 'Trash' && (
                        <motion.button
                            whileHover={{ scale: 1.1 }}
                            onClick={() => onAction('Restore')}
                            className="btn-ghost"
                            style={{ padding: '8px', color: 'var(--success)' }}
                            title="Restore"
                        >
                            <RotateCcw size={18} />
                        </motion.button>
                    )}
                    {email.folder === 'Archive' && (
                        <motion.button
                            whileHover={{ scale: 1.1 }}
                            onClick={() => onAction('Undo')}
                            className="btn-ghost"
                            style={{ padding: '8px', color: 'var(--success)' }}
                            title="Undo"
                        >
                            <RotateCcw size={18} />
                        </motion.button>
                    )}
                    {email.folder !== 'Trash' && email.folder !== 'Archive' && (
                        <motion.button
                            whileHover={{ scale: 1.1 }}
                            onClick={() => onAction('Archive')}
                            className="btn-ghost"
                            style={{ padding: '8px' }}
                            title="Archive"
                        >
                            <Archive size={18} />
                        </motion.button>
                    )}
                    <motion.button
                        whileHover={{ scale: 1.1 }}
                        onClick={() => onAction('Trash')}
                        className="btn-ghost"
                        style={{ padding: '8px', color: 'var(--accent)' }}
                        title="Delete"
                    >
                        <Trash2 size={18} />
                    </motion.button>
                </div>
            </div>

            {/* Fixed Header & Info Section */}
            <div style={{ padding: '16px 20px', paddingBottom: '0', flexShrink: 0 }}>
                <div style={{ marginBottom: '16px' }}>
                    <motion.h2
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        style={{
                            fontSize: '1.25rem',
                            fontWeight: '600',
                            color: 'var(--text-main)',
                            marginBottom: '16px',
                            lineHeight: '1.3',
                            fontFamily: "'Wix Madefor Display', sans-serif"
                        }}
                    >
                        {email.subject}
                    </motion.h2>

                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px',
                        background: 'var(--bg-secondary)',
                        borderRadius: '12px',
                        border: '1px solid var(--border-glass)',
                        position: 'relative'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                            <div style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '10px',
                                background: 'linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontWeight: '700',
                                fontSize: '0.9rem',
                                flexShrink: 0
                            }}>
                                {senderInitial}
                            </div>
                            <div style={{ minWidth: 0 }}>
                                <div style={{ fontWeight: '600', color: 'var(--text-main)', fontSize: '0.95rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {displayIdentifier}
                                </div>
                                <div style={{
                                    fontSize: '0.8rem',
                                    color: 'var(--text-dim)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    marginTop: '2px'
                                }}>
                                    <span>to {recipientLabel}</span>
                                    <button
                                        onClick={() => setShowDetails(!showDetails)}
                                        style={{
                                            background: 'var(--bg-glass)',
                                            border: 'none',
                                            borderRadius: '4px',
                                            padding: '2px',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            color: 'var(--text-dim)',
                                            transition: 'background 0.2s'
                                        }}
                                        className="btn-hover-light"
                                    >
                                        <ChevronDown size={14} style={{ transform: showDetails ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                                    </button>
                                </div>

                                <AnimatePresence>
                                    {showDetails && (
                                        <motion.div
                                            initial={{ opacity: 0, y: -10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -10 }}
                                            style={{
                                                position: 'absolute',
                                                top: '100%',
                                                left: '52px',
                                                zIndex: 1000,
                                                background: 'var(--bg-main)',
                                                border: '1px solid var(--border-glass)',
                                                borderRadius: '12px',
                                                boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                                                padding: '16px',
                                                marginTop: '8px',
                                                minWidth: '320px',
                                                fontSize: '0.85rem'
                                            }}
                                        >
                                            <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: '8px 16px' }}>
                                                <div style={{ color: 'var(--text-dim)', textAlign: 'right' }}>from:</div>
                                                <div style={{ color: 'var(--text-main)', fontWeight: '500' }}>{fromEmail}</div>
                                                <div style={{ color: 'var(--text-dim)', textAlign: 'right' }}>to:</div>
                                                <div style={{ color: 'var(--text-main)', fontWeight: '500' }}>{toEmail}</div>

                                                {email.cc && (
                                                    <>
                                                        <div style={{ color: 'var(--text-dim)', textAlign: 'right' }}>cc:</div>
                                                        <div style={{ color: 'var(--text-main)', fontWeight: '500' }}>{email.cc}</div>
                                                    </>
                                                )}

                                                {email.bcc && (
                                                    <>
                                                        <div style={{ color: 'var(--text-dim)', textAlign: 'right' }}>bcc:</div>
                                                        <div style={{ color: 'var(--text-main)', fontWeight: '500' }}>{email.bcc}</div>
                                                    </>
                                                )}
                                                <div style={{ color: 'var(--text-dim)', textAlign: 'right' }}>date:</div>
                                                <div style={{ color: 'var(--text-main)', fontWeight: '500' }}>{formatDate(email.time)}</div>
                                                <div style={{ color: 'var(--text-dim)', textAlign: 'right' }}>subject:</div>
                                                <div style={{ color: 'var(--text-main)', fontWeight: '500' }}>{email.subject}</div>
                                                <div style={{ color: 'var(--text-dim)', textAlign: 'right' }}>mailed-by:</div>
                                                <div style={{ color: 'var(--text-main)', fontWeight: '500' }}>xevyte.co</div>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>{formatDate(email.time)}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Scrollable Email Content Section */}
            <div className="scrollbar-hide" style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', minHeight: 0 }}>


                {/* Body Content */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                    style={{
                        fontSize: '1rem',
                        color: 'var(--text-main)',
                        lineHeight: '1.6',
                        background: 'transparent',
                        padding: '8px 4px',
                        minHeight: '200px',
                        overflowWrap: 'break-word',
                        wordBreak: 'break-word'
                    }}
                >
                    <div
                        className="mail-content-body"
                        dangerouslySetInnerHTML={{ __html: email.body || "No content provided." }}
                    />

                </motion.div>

                {/* Attachments Section */}
                {email.attachments && email.attachments.length > 0 && (
                    <div style={{ marginTop: '40px', paddingTop: '24px', borderTop: '1px solid var(--border-glass)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                            <h3 style={{ fontSize: '0.9rem', fontWeight: '700', color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Attachments ({email.attachments.length})
                            </h3>
                            {email.attachments.length > 1 && (
                                <button
                                    onClick={() => {
                                        email.attachments.forEach((att, i) => {
                                            setTimeout(() => {
                                                const link = document.createElement('a');
                                                link.href = `${API_BASE}/mails/attachment/${att.id}?token=${user.token}`;
                                                link.download = att.fileName;
                                                document.body.appendChild(link);
                                                link.click();
                                                document.body.removeChild(link);
                                            }, i * 300);
                                        });
                                    }}
                                    style={{ fontSize: '0.8rem', color: 'var(--primary)', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', background: 'none', border: 'none' }}
                                >
                                    <Download size={14} /> Download All
                                </button>
                            )}
                        </div>

                        {/* Image Previews */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
                            {email.attachments.filter(att => att.contentType?.startsWith('image/')).map((att, idx) => (
                                <motion.div
                                    key={`img-${idx}`}
                                    whileHover={{ scale: 1.02 }}
                                    style={{
                                        width: '120px',
                                        height: '120px',
                                        borderRadius: '12px',
                                        overflow: 'hidden',
                                        border: '1px solid var(--border-glass)',
                                        cursor: 'pointer',
                                        position: 'relative'
                                    }}
                                    onClick={() => {
                                        const link = document.createElement('a');
                                        link.href = `${API_BASE}/mails/attachment/${att.id}?token=${user.token}`;
                                        link.download = att.fileName;
                                        link.click();
                                    }}
                                >
                                    <img
                                        src={`${API_BASE}/mails/attachment/${att.id}?token=${user.token}`}
                                        alt={att.fileName}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.5)', color: 'white', fontSize: '10px', padding: '4px', textAlign: 'center' }}>
                                        Preview
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
                            {email.attachments.map((att, idx) => (
                                <motion.div
                                    key={idx}
                                    whileHover={{ y: -4, borderColor: 'var(--primary-glow)' }}
                                    style={{
                                        background: 'var(--bg-secondary)',
                                        border: '1px solid var(--border-glass)',
                                        borderRadius: '12px',
                                        padding: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '12px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        position: 'relative'
                                    }}
                                    onClick={() => {
                                        const link = document.createElement('a');
                                        link.href = `${API_BASE}/mails/attachment/${att.id}?token=${user.token}`;
                                        link.download = att.fileName;
                                        link.click();
                                    }}
                                >
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '8px',
                                        background: 'var(--bg-main)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'var(--primary)',
                                        flexShrink: 0
                                    }}>
                                        {att.contentType?.startsWith('image/') ? <ImageIcon size={20} /> : <FileText size={20} />}
                                    </div>
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                        <div style={{ fontSize: '0.85rem', fontWeight: '600', color: 'var(--text-main)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {att.fileName}
                                        </div>
                                        <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)' }}>
                                            {(att.fileSize / 1024).toFixed(1)} KB
                                        </div>
                                    </div>
                                    <div style={{ color: 'var(--text-dim)', padding: '4px' }}>
                                        <Download size={14} />
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Quick Actions */}
                <div style={{ display: 'flex', gap: '10px', marginTop: '32px', alignItems: 'center' }}>
                    <motion.button
                        whileHover={{ scale: 1.05, backgroundColor: 'var(--bg-glass-hover)' }}
                        whileTap={{ scale: 0.95 }}
                        onClick={onReply}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 20px',
                            borderRadius: '24px',
                            border: '1px solid var(--border-glass)',
                            background: 'transparent',
                            color: 'var(--text-main)',
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            cursor: 'pointer'
                        }}
                    >
                        <Reply size={16} /> <span>Reply</span>
                    </motion.button>

                    <motion.button
                        whileHover={{ scale: 1.05, backgroundColor: 'var(--bg-glass-hover)' }}
                        whileTap={{ scale: 0.95 }}
                        onClick={onReplyAll}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 20px',
                            borderRadius: '24px',
                            border: '1px solid var(--border-glass)',
                            background: 'transparent',
                            color: 'var(--text-main)',
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            cursor: 'pointer'
                        }}
                    >
                        <ReplyAll size={16} /> <span>Reply All</span>
                    </motion.button>

                    <motion.button
                        whileHover={{ scale: 1.05, backgroundColor: 'var(--bg-glass-hover)' }}
                        whileTap={{ scale: 0.95 }}
                        onClick={onForward}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 20px',
                            borderRadius: '24px',
                            border: '1px solid var(--border-glass)',
                            background: 'transparent',
                            color: 'var(--text-main)',
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            cursor: 'pointer'
                        }}
                    >
                        <Forward size={16} /> <span>Forward</span>
                    </motion.button>
                </div>
            </div>
        </motion.div>
    );
};

export default MailViewer;
